import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, Eye, Phone, Mail, Search, Download, Briefcase, User, Building2, Calendar } from 'lucide-react';
import { getUser } from '@/utils/auth';
import api from '@/utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExecutiveApplications = () => {
  const user = getUser();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalApplications, setTotalApplications] = useState(0);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    fetchApplications();
  }, [page]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/executive/applications?executive_id=${user.id}&skip=${(page - 1) * limit}&limit=${limit}`);
      setApplications(response.data.applications || []);
      setTotalApplications(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const viewApplicationDetails = async (application) => {
    try {
      // Fetch full job and applicant details
      const [jobResponse, profileResponse] = await Promise.all([
        api.get(`/jobs/${application.job_id}`),
        api.get(`/job-seeker/profile/${application.job_seeker_id}`)
      ]);
      
      setSelectedApplication({
        ...application,
        job: jobResponse.data,
        profile: profileResponse.data
      });
      setShowDetailDialog(true);
    } catch (error) {
      setSelectedApplication(application);
      setShowDetailDialog(true);
    }
  };

  const downloadCV = (cvPath) => {
    window.open(`${API_URL}${cvPath}`, '_blank');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'applied':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Applied</Badge>;
      case 'shortlisted':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Shortlisted</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
      case 'hired':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Hired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredApplications = searchQuery
    ? applications.filter(app =>
        app.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.job_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.applicant_mobile?.includes(searchQuery)
      )
    : applications;

  const totalPages = Math.ceil(totalApplications / limit);

  if (loading && page === 1) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
            Applications
          </h1>
          <p className="text-slate-600">Job applications in your allocated districts</p>
        </div>
        <Badge variant="outline" className="px-3 py-1.5">
          <ClipboardList className="w-4 h-4 mr-1" /> {totalApplications} Applications
        </Badge>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search by applicant, job title, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </Card>

      {/* Applications Table */}
      <Card className="border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Sl.No</TableHead>
              <TableHead>Applicant</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Job Title</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Applied On</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredApplications.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600">No applications found in your allocated districts</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredApplications.map((app, index) => (
                <TableRow key={app.id}>
                  <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      {app.applicant_name || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3 text-slate-400" />
                      {app.applicant_mobile || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Briefcase className="w-3 h-3 text-slate-400" />
                      {app.job_title || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3 h-3 text-slate-400" />
                      {app.company_name || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>{getStatusBadge(app.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => viewApplicationDetails(app)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-600">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalApplications)} of {totalApplications} applications
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Application Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4 mt-4">
              {/* Applicant Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" /> Applicant Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium">{selectedApplication.profile?.full_name || selectedApplication.applicant_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Mobile</p>
                    <p className="font-medium">{selectedApplication.applicant_mobile || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">{selectedApplication.profile?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Applied On</p>
                    <p className="font-medium">
                      {selectedApplication.applied_at ? new Date(selectedApplication.applied_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
                {selectedApplication.profile?.cv && (
                  <div className="mt-3">
                    <Button
                      size="sm"
                      onClick={() => downloadCV(selectedApplication.profile.cv)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download CV
                    </Button>
                  </div>
                )}
              </div>

              {/* Job Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Job Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Job Title</p>
                    <p className="font-medium">{selectedApplication.job?.job_title || selectedApplication.job_title || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Company</p>
                    <p className="font-medium">{selectedApplication.job?.company_name || selectedApplication.company_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Salary Range</p>
                    <p className="font-medium">
                      {selectedApplication.job ? 
                        `₹${selectedApplication.job.min_salary?.toLocaleString()} - ₹${selectedApplication.job.max_salary?.toLocaleString()}` 
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Experience Required</p>
                    <p className="font-medium">
                      {selectedApplication.job ? 
                        `${selectedApplication.job.min_experience} - ${selectedApplication.job.max_experience} years` 
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-500">Application Status</p>
                  <div className="mt-1">{getStatusBadge(selectedApplication.status)}</div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExecutiveApplications;
